import { usePlacementZoomAndPan } from "@design-lab/features/canvas";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { trackZoomClicked } from "@design-lab/shared/lib/analytics";
import { Button } from "@design-lab/shared/ui";
import { Minus, Plus, ZoomIn, ZoomOut } from "lucide-react";

type RefineZoomProps = {
  maxZoom?: number;
};

const ZOOM_STEP = 0.25;
const MIN_ZOOM = 1;

export function RefineZoom({ maxZoom = 10 }: RefineZoomProps) {
  const { isDesktop } = useResponsive();
  const { zoom, setZoom } = usePlacementZoomAndPan(MIN_ZOOM, maxZoom);
  const zoomPercentage = Math.round(zoom * 100);

  const handleZoomIn = () => {
    const newZoom = Math.min(zoom + ZOOM_STEP, maxZoom);
    setZoom(newZoom);
    trackZoomClicked(newZoom);
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(zoom - ZOOM_STEP, MIN_ZOOM);
    setZoom(newZoom);
    trackZoomClicked(newZoom);
  };

  const canZoomIn = zoom < maxZoom;
  const canZoomOut = zoom > MIN_ZOOM;

  if (!isDesktop) {
    return (
      <div className="flex h-10 w-20 items-center rounded-lg bg-white shadow-md">
        <button
          data-testid="refine-zoom-out-button"
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-l-lg p-1.5 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={handleZoomOut}
          disabled={!canZoomOut}
        >
          <ZoomOut className="h-5 w-5" />
        </button>
        <div className="h-8 w-px bg-gray-100"></div>
        <button
          data-testid="refine-zoom-in-button"
          className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-r-lg p-1.5 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={handleZoomIn}
          disabled={!canZoomIn}
        >
          <ZoomIn className="h-5 w-5" />
        </button>
      </div>
    );
  }

  return (
    <div className="bg-background flex h-10 items-center gap-1 rounded-md border p-1 shadow-sm">
      <Button
        data-testid="refine-zoom-out-button"
        variant="icon"
        size="sm"
        className="h-8 w-8 rounded-md"
        onPress={handleZoomOut}
        isDisabled={!canZoomOut}
        aria-label="Zoom out"
      >
        <Minus className="size-4" />
      </Button>

      <div
        role="status"
        aria-label="Zoom percentage"
        className="flex h-8 w-14 select-none items-center justify-center rounded-md border bg-white text-sm font-medium text-gray-700"
      >
        {zoomPercentage}%
      </div>

      <Button
        data-testid="refine-zoom-in-button"
        variant="icon"
        size="sm"
        className="h-8 w-8 rounded-md"
        onPress={handleZoomIn}
        isDisabled={!canZoomIn}
        aria-label="Zoom in"
      >
        <Plus className="size-4" />
      </Button>
    </div>
  );
}
